import moment, { Moment } from 'moment-timezone';
import { useContext, useEffect, useMemo, useState } from 'react';
import { selectors as envSelectors } from 'wpt-duck-env';
import { AppContext } from '../../AppContext';
import { getCapabilities } from '../api/capabilities';

export type TimeExtentString = string & { _MARKER: TimeExtentString };

const splitTimeExtentFragment = (timeExtentFragment: string) => {
  const fragment = moment(timeExtentFragment, moment.ISO_8601, true);
  if (fragment.isValid()) return [fragment];
  const [wmsStart, wmsEnd, wmsPeriod] = timeExtentFragment.split('/');
  const start = moment(wmsStart, moment.ISO_8601, true);
  const end = moment(wmsEnd, moment.ISO_8601, true);
  const period = moment.duration(wmsPeriod);
  if (!start.isValid() || !end.isValid() || !period.isValid()) {
    return [];
  }
  const current = start.clone();
  const ret: Moment[] = [];
  while (!end.isBefore(current)) {
    ret.push(current.clone());
    current.add(period);
  }
  return ret;
};

const splitTimeExtent = (timeExtentString: TimeExtentString) => {
  const ret: Moment[] = [];
  timeExtentString
    .split(',')
    .forEach((fragment) => ret.push(...splitTimeExtentFragment(fragment)));
  return ret;
};

export interface CapabilityInput {
  timextent?: TimeExtentString;
  layerId: string;
  reftime?: string;
}

export interface Capability {
  times?: Moment[];
  refTime?: string;
  layerId: string;
}

export interface Capabilities {
  [key: string]: Capability | undefined;
}

export const useCapabilities = (
  layers: string[],
  capabilityId: string,
  events?: EventTarget
) => {
  const { state } = useContext(AppContext);
  const [capabilityInputs, setCapabilityInputs] = useState<{
    [key: string]: CapabilityInput;
  }>({});
  const host: string = envSelectors.getHost(state);
  const [refreshToken, setRefreshToken] = useState({});

  useEffect(() => {
    const refreshListener = () => setRefreshToken({});
    events?.addEventListener('refresh', refreshListener);
    return () => events?.removeEventListener('refresh', refreshListener);
  }, [events]);

  useEffect(() => {
    if (host.indexOf('undefined') !== -1) return;
    const loadData = async () => {
      const result = await getCapabilities(host, capabilityId, layers);
      if (result.ok) {
        setCapabilityInputs(await result.json());
      } else {
        setCapabilityInputs({});
      }
    };
    loadData();
  }, [layers.join(), host, refreshToken]);

  const capabilities = useMemo(() => {
    const ret: Capabilities = {};
    Object.keys(capabilityInputs).forEach((capKey) => {
      const cap = capabilityInputs[capKey];
      ret[capKey] = {
        refTime: cap.reftime,
        times: cap.timextent && splitTimeExtent(cap.timextent),
        layerId: cap.layerId,
      };
    });
    return ret;
  }, [capabilityInputs]);

  return capabilities;
};
